/**
 * Hosted AI client state: AI mode (hosted vs own key), the healthcare data-use consent,
 * the plan's credit allowance and a tiny "gate" that lets non-React code (api/ai.js) ask
 * the UI to show the consent dialog or an upgrade modal and wait for the answer.
 *
 * Storage (localStorage, always wrapped in try/catch; private windows may throw):
 *   icd-ai-mode-v1     { [scope]: 'hosted' | 'byok' }          scope = 'u:<userId>' or 'guest'
 *   icd-ai-consent-v1  { [scope]: { v, at } }                 consent per user and per browser
 * Nothing here is secret; API keys stay in lib/byok.js.
 */
import { api } from '../api/client';
import { hasAiKey } from './byok';

const MODE_KEY = 'icd-ai-mode-v1';
const CONSENT_KEY = 'icd-ai-consent-v1';
export const CONSENT_VERSION = 1;

/** Default credit weights (the server's `weights` from /ai/allowance win when present). */
export const DEFAULT_WEIGHTS = {
  explain: 1,
  'query-builder': 1,
  'guidelines-chat': 1,
  'medical-necessity': 2,
  'note-to-codes': 3,
  'semantic-search': 1,
};

export const FEATURE_LABELS = {
  explain: 'Explain with AI',
  'query-builder': 'Query Builder',
  'guidelines-chat': 'Guidelines Chat',
  'medical-necessity': 'Medical Necessity',
  'note-to-codes': 'Note → Codes',
  'semantic-search': 'Semantic search',
};

/**
 * Fallback plan comparison (the AI_HOSTED_CONTRACT defaults), shown only while /ai/allowance loads or if it
 * fails. Use `planComparison(data)` and `planNumbers(data)` for the live, admin-editable values.
 */
export const PLAN_COMPARISON = [
  { id: 'guest', label: 'Guest', credits: '5 credits a day', byok: false },
  { id: 'free', label: 'Free', credits: '60 credits a month', byok: false },
  { id: 'pro', label: 'Pro', credits: '300 credits a month', byok: true },
  { id: 'team', label: 'Team', credits: '400 credits per seat, pooled', byok: true },
];

export const PLAN_LABELS = { guest: 'Guest', free: 'Free', pro: 'Pro', team: 'Team' };

const FALLBACK_PLANS = {
  guest: { hosted: true, period: 'day', credits: 5, dailyMax: null, byok: false, modelLabel: null },
  free: { hosted: true, period: 'month', credits: 60, dailyMax: 15, byok: false, modelLabel: null },
  pro: { hosted: true, period: 'month', credits: 300, dailyMax: 60, byok: true, modelLabel: null },
  team: { hosted: true, period: 'month', credits: null, creditsPerSeat: 400, poolMax: 1000, pooled: true, dailyMax: 80, byok: true, modelLabel: null },
};

const num = (v) => (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null);

/** Live plan settings from /ai/allowance `plans` (falls back to the contract defaults while loading). */
export function livePlans(data = allowance.data) {
  const live = data && data.plans && typeof data.plans === 'object' ? data.plans : null;
  const out = {};
  Object.keys(FALLBACK_PLANS).forEach((id) => {
    const p = live && live[id] && typeof live[id] === 'object' ? live[id] : null;
    out[id] = p ? { ...FALLBACK_PLANS[id], ...p } : FALLBACK_PLANS[id];
  });
  return { plans: out, live: !!live };
}

function creditsText(id, p) {
  if (p.hosted === false) return 'Not included';
  const per = p.period === 'day' ? 'a day' : 'a month';
  if (id === 'team') {
    const seat = num(p.creditsPerSeat);
    if (seat === null) return num(p.credits) !== null ? `${num(p.credits).toLocaleString()} credits ${per}` : 'Included';
    const pool = num(p.poolMax);
    return `${seat.toLocaleString()} credits per seat${p.pooled ? ', pooled' : ''}${pool !== null ? ` (up to ${pool.toLocaleString()} ${per})` : ''}`;
  }
  const c = num(p.credits);
  return c === null ? 'Included' : `${c.toLocaleString()} credits ${per}`;
}

/** Rows for plan comparison tables: [{ id, label, credits, byok, modelLabel }]. */
export function planComparison(data = allowance.data) {
  const { plans, live } = livePlans(data);
  if (!live) return PLAN_COMPARISON.map((p) => ({ ...p, modelLabel: null }));
  return PLAN_COMPARISON.map(({ id, label }) => ({ id, label, credits: creditsText(id, plans[id]), byok: !!plans[id].byok, modelLabel: plans[id].modelLabel || null }));
}

/**
 * Numbers for upgrade copy: { freeCredits, freePeriod, proCredits, proPeriod, multiplier ("10×" or null), proModel }.
 */
export function planNumbers(data = allowance.data) {
  const { plans } = livePlans(data);
  const free = plans.free;
  const pro = plans.pro;
  const freeCredits = free.hosted === false ? 0 : num(free.credits);
  const proCredits = pro.hosted === false ? 0 : num(pro.credits);
  let multiplier = null;
  if (freeCredits && proCredits && free.period === pro.period && proCredits > freeCredits) {
    const x = proCredits / freeCredits;
    multiplier = Number.isInteger(x) ? `${x}×` : `${Math.round(x * 10) / 10}×`;
  }
  return {
    freeCredits,
    freePeriod: free.period === 'day' ? 'day' : 'month',
    proCredits,
    proPeriod: pro.period === 'day' ? 'day' : 'month',
    multiplier,
    proModel: pro.modelLabel || null,
  };
}

/* ------------------------------------------------------------------ tiny store helpers */

function hasWindow() {
  return typeof window !== 'undefined';
}

function readJson(key) {
  try {
    const raw = hasWindow() ? window.localStorage.getItem(key) : null;
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

const listeners = new Set();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // A listener failing must not break the others.
    }
  });
}

/** Subscribe to any hosted-AI state change (mode, consent, allowance, gate). */
export function subscribeAiHosted(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getAiHostedVersion() {
  return version;
}

if (hasWindow()) {
  try {
    window.addEventListener('storage', (e) => {
      if (!e || e.key === null || e.key === MODE_KEY || e.key === CONSENT_KEY) emit();
    });
    // lib/byok.js fires this when a key is saved/removed, which changes the effective mode.
    window.addEventListener('icd-byok-change', () => emit());
  } catch {
    // Non-browser environment.
  }
}

/* ------------------------------------------------------------------ identity */

let identity = { userId: null, plan: null };

/** Set by useAiAllowance from AuthContext so consent and mode are scoped per user. */
export function setAiIdentity(userId, plan) {
  const next = { userId: userId || null, plan: plan || null };
  if (next.userId === identity.userId && next.plan === identity.plan) return;
  const changed = next.userId !== identity.userId;
  identity = next;
  if (changed) {
    allowance = { ...allowance, data: null, status: 'idle', fetchedAt: 0 };
  }
  emit();
}

function scope() {
  return identity.userId ? `u:${identity.userId}` : 'guest';
}

/* ------------------------------------------------------------------ allowance */

let allowance = { data: null, status: 'idle', error: null, fetchedAt: 0 };
let inflight = null;
let inflightScope = null;
const STALE_MS = 60 * 1000;

export function getAllowanceState() {
  return allowance;
}

function setAllowance(patch) {
  allowance = { ...allowance, ...patch };
  emit();
}

/** GET /ai/allowance (deduped; cached for a minute unless `force`). Resolves to the data or null. */
export function fetchAllowance({ force = false } = {}) {
  if (inflight && inflightScope === scope()) return inflight;
  if (!force && allowance.data && Date.now() - allowance.fetchedAt < STALE_MS) return Promise.resolve(allowance.data);
  if (!allowance.data) setAllowance({ status: 'loading', error: null });
  const forScope = scope();
  inflightScope = forScope;
  const request = api
    .get('/ai/allowance')
    .then(({ data }) => {
      if (forScope !== scope()) return null;
      setAllowance({ data: data && typeof data === 'object' ? data : null, status: 'ready', error: null, fetchedAt: Date.now() });
      return allowance.data;
    })
    .catch((e) => {
      setAllowance({ status: 'error', error: e, fetchedAt: Date.now() });
      return null;
    })
    .finally(() => {
      if (inflight === request) inflight = null;
    });
  inflight = request;
  return request;
}

function patchHosted(patch) {
  if (!allowance.data) return;
  const hosted = { ...(allowance.data.hosted || {}), ...patch };
  setAllowance({ data: { ...allowance.data, hosted } });
}

/** Apply `X-AI-Credits-Remaining` / `meta.credits` from a hosted response. */
export function applyCreditsFromResponse(res) {
  if (!res) return;
  const headers = res.headers || {};
  const raw = headers['x-ai-credits-remaining'] !== undefined ? headers['x-ai-credits-remaining'] : headers['X-AI-Credits-Remaining'];
  const meta = res.data && res.data.meta && res.data.meta.credits;
  const remaining = raw !== undefined && raw !== null && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : meta && Number.isFinite(Number(meta.remaining)) ? Number(meta.remaining) : null;
  if (remaining !== null) {
    const patch = { remaining: Math.max(0, remaining) };
    if (meta && Number.isFinite(Number(meta.used))) patch.used = Number(meta.used);
    if (meta && Number.isFinite(Number(meta.limit))) patch.limit = Number(meta.limit);
    patchHosted(patch);
  }
  // Confirm with the server (daily counters, bonus) without blocking the UI.
  fetchAllowance({ force: true });
}

/** Called on 402 AI_LIMIT_REACHED with the error body. */
export function markLimitReached(details = {}) {
  const patch = { remaining: 0 };
  if (Number.isFinite(Number(details.used))) patch.used = Number(details.used);
  if (Number.isFinite(Number(details.limit))) patch.limit = Number(details.limit);
  if (details.resetAt) patch.resetAt = details.resetAt;
  patchHosted(patch);
}

/** Normalised view of the allowance for meters and chips. */
export function creditsView(data) {
  const hosted = (data && data.hosted) || {};
  const limit = Math.max(0, Number(hosted.limit) || 0);
  const bonus = Math.max(0, Number(hosted.bonus) || 0);
  const used = Math.max(0, Number(hosted.used) || 0);
  const total = limit + bonus;
  const remaining = Number.isFinite(Number(hosted.remaining)) && hosted.remaining !== null && hosted.remaining !== undefined ? Math.max(0, Number(hosted.remaining)) : Math.max(0, total - used);
  const period = hosted.period === 'day' ? 'day' : 'month';
  const dailyMax = Number(hosted.dailyMax) || 0;
  const dailyUsed = Number(hosted.dailyUsed) || 0;
  const dailyLeft = dailyMax ? Math.max(0, dailyMax - dailyUsed) : null;
  return {
    known: !!(data && data.hosted),
    enabled: hosted.enabled !== false,
    limit,
    bonus,
    used: Math.min(used, total || used),
    total,
    remaining,
    pct: total ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100)) : 0,
    period,
    periodLabel: period === 'day' ? 'today' : 'this month',
    resetAt: hosted.resetAt || null,
    dailyMax,
    dailyUsed,
    dailyLeft,
    model: hosted.model || null,
    plan: (data && data.plan) || identity.plan || 'guest',
  };
}

export function weightOf(feature, data) {
  const w = data && data.weights && data.weights[feature];
  if (Number.isFinite(Number(w))) return Number(w);
  return DEFAULT_WEIGHTS[feature] !== undefined ? DEFAULT_WEIGHTS[feature] : 1;
}

/** "Resets in 5 h" (under a day) or "Resets Nov 1". */
export function formatReset(resetAt) {
  if (!resetAt) return '';
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return '';
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return 'Resets shortly';
  const h = Math.floor(ms / 3600000);
  const m = Math.max(1, Math.round((ms % 3600000) / 60000));
  if (h < 1) return `Resets in ${m} min`;
  if (h < 24) return `Resets in ${h} h${m && h < 6 ? ` ${m} min` : ''}`;
  return `Resets ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function formatResetDate(resetAt) {
  if (!resetAt) return '';
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/* ------------------------------------------------------------------ mode */

/** Stored preference, or a default: own key if one is already set up in this browser, else hosted. */
export function getAiModePreference() {
  const map = readJson(MODE_KEY);
  const v = map[scope()];
  if (v === 'hosted' || v === 'byok') return v;
  return hasAiKey() ? 'byok' : 'hosted';
}

export function setAiModePreference(mode) {
  const map = readJson(MODE_KEY);
  map[scope()] = mode === 'byok' ? 'byok' : 'hosted';
  writeJson(MODE_KEY, map);
  emit();
}

/** Whether the current plan may use its own key (server's `byok.allowed`, else the auth plan). */
export function byokAllowed(data = allowance.data) {
  if (data && data.byok && typeof data.byok.allowed === 'boolean') return data.byok.allowed;
  const plan = (data && data.plan) || identity.plan;
  return plan === 'pro' || plan === 'team';
}

/** 'byok' only when preferred, allowed for the plan and a key is configured; otherwise 'hosted'. */
export function getEffectiveAiMode(data = allowance.data) {
  if (getAiModePreference() !== 'byok') return 'hosted';
  if (!byokAllowed(data)) return 'hosted';
  return hasAiKey() ? 'byok' : 'hosted';
}

/* ------------------------------------------------------------------ consent */

export function getConsent() {
  const entry = readJson(CONSENT_KEY)[scope()];
  if (entry && entry.v >= CONSENT_VERSION && entry.at) return entry;
  return null;
}

export function hasConsent() {
  return !!getConsent();
}

export function giveConsent() {
  const map = readJson(CONSENT_KEY);
  map[scope()] = { v: CONSENT_VERSION, at: new Date().toISOString() };
  writeJson(CONSENT_KEY, map);
  emit();
}

export function clearConsent() {
  const map = readJson(CONSENT_KEY);
  delete map[scope()];
  writeJson(CONSENT_KEY, map);
  emit();
}

/* ------------------------------------------------------------------ gate (dialogs) */

let gate = null; // { id, kind, payload, resolve }
let gateSeq = 0;
const hosts = [];

export function getGate() {
  return gate;
}

/** Each mounted AiGateHost registers; only the first one renders the dialogs. */
export function registerGateHost(id) {
  hosts.push(id);
  emit();
  return () => {
    const i = hosts.indexOf(id);
    if (i >= 0) hosts.splice(i, 1);
    if (!hosts.length && gate) {
      const g = gate;
      gate = null;
      g.resolve(null);
    }
    emit();
  };
}

export function isGateLeader(id) {
  return hosts[0] === id;
}

/**
 * Ask the UI to show a dialog. Resolves with the user's choice, or `null` when dismissed
 * or when no host is mounted (the caller then falls back to the inline error).
 */
export function openGate(kind, payload) {
  if (!hosts.length) return Promise.resolve(null);
  if (gate) gate.resolve(null);
  return new Promise((resolve) => {
    gateSeq += 1;
    const id = gateSeq;
    gate = {
      id,
      kind,
      payload: payload || {},
      resolve: (value) => {
        if (gate && gate.id === id) gate = null;
        resolve(value === undefined ? null : value);
        emit();
      },
    };
    emit();
  });
}

export function closeGate(value) {
  if (gate) gate.resolve(value);
}
